<script setup>
import { ref, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsQualificationsForm from '@/Components/Forms/SettingsQualificationsForm.vue'

const props = defineProps({
  user: Object,
  subjects: {
    type: Array,
    default: () => []
  },
  qualificationOptions: {
    type: Object,
    default: () => ({})
  },
  qualificationData: {
    type: Object,
    default: () => null
  }
})

// New structure matching onboarding: degrees array + a_levels array
const qualification = ref({
  degrees: [],
  a_levels: [],
  additional_qualifications: ''
})

const qualificationFormRef = ref(null)
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Populate qualification from qualificationData prop (from qualifications table)
onMounted(() => {
  if (props.qualificationData) {
    qualification.value = {
      degrees: props.qualificationData.degrees || [],
      a_levels: props.qualificationData.a_levels || [],
      additional_qualifications: props.qualificationData.additional_qualifications || ''
    }
  }
})

const updateQualification = (newQualification) => {
  qualification.value = newQualification
}

const saveQualification = async () => {
  if (qualificationFormRef.value && !qualificationFormRef.value.validate()) {
    errors.value = { general: 'Please fill in all required fields' }
    return
  }

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/qualifications', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(qualification.value)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Map Laravel errors (arrays) to flat error messages (strings)
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Qualifications updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="Object.keys(errors).length > 0" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5">
        <div class="flex items-start">
          <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">error</span>
          </div>
          <div class="flex-1">
            <p class="text-red-900 font-semibold">{{ errors.general ? 'Error' : 'Validation Error' }}</p>
            <p v-if="errors.general" class="text-red-700 text-sm">{{ errors.general }}</p>
            <ul v-else class="text-red-700 text-sm mt-1 space-y-1">
              <li v-for="(message, field) in errors" :key="field" class="flex items-start">
                <span class="text-red-400 mr-2">•</span>
                {{ message }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Qualifications Form -->
      <SettingsQualificationsForm
        ref="qualificationFormRef"
        :model-value="qualification"
        @update:model-value="updateQualification"
        :errors="errors"
        :subjects="subjects"
        :qualification-options="qualificationOptions"
      />

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveQualification"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
